import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  ShoppingCart,
  ExpandMore,
  ExpandLess,
  LocalDining,
  Restaurant,
} from '@mui/icons-material';
import { menuItems } from '../../data';
import { getIconComponent } from '../../App';
import { MenuItem as MenuItemType } from '../../types';

interface HeaderProps {
  cartItemsCount?: number;
}

const Header: React.FC<HeaderProps> = ({ cartItemsCount = 0 }) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [dropdownAnchor, setDropdownAnchor] = React.useState<{ [key: string]: HTMLElement | null }>({});
  const [expandedMobileMenus, setExpandedMobileMenus] = React.useState<{ [key: string]: boolean }>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileSubmenuToggle = (menuId: string) => {
    setExpandedMobileMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLButtonElement>, menuId: string) => {
    setDropdownAnchor(prev => ({ ...prev, [menuId]: event.currentTarget }));
  };

  const handleDropdownClose = (menuId: string) => {
    setDropdownAnchor(prev => ({ ...prev, [menuId]: null }));
  };

  return (
    <AppBar position="static" sx={{ 
    bgcolor: '#FAFAFA', 
    boxShadow: 'none',
    borderBottom: '1px solid rgba(0,0,0,0.15)'
  }}>
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            color: '#000',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Restaurant sx={{ color: 'red', fontSize: '1.8rem' }} />
          Goodiya Cooking Receipe
        </Typography>

        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          sx={{ 
            display: { xs: 'flex', md: 'none' },
            color: '#000'
          }}
          onClick={handleMobileMenuOpen}
        >
          <MenuIcon />
        </IconButton>

        {/* Desktop Menu */}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          {menuItems.map((item: MenuItemType) => (
            <Box key={item.id} sx={{ position: 'relative' }}>
              <Button
                color="inherit"
                component={item.submenu ? 'button' : Link}
                to={item.submenu ? undefined : item.path}
                onClick={item.submenu ? (e: React.MouseEvent<HTMLButtonElement>) => handleDropdownOpen(e, item.id) : undefined}
                endIcon={item.submenu ? <ExpandMore sx={{ fontSize: '1rem' }} /> : null}
                sx={{ 
                  color: '#000', 
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2,
                  border: '1px solid transparent',
                  borderColor: 'transparent',
                  '&:hover': {
                    color: 'red',
                  },
                }}
              >
                {item.name}
              </Button>
              
              {/* Dropdown Menu */}
              {item.submenu && (
                <Menu
                  anchorEl={dropdownAnchor[item.id]}
                  open={Boolean(dropdownAnchor[item.id])}
                  onClose={() => handleDropdownClose(item.id)}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '& .MuiMenuItem-root': {
                        color: '#000',
                        textTransform: 'none',
                        '&:hover': {
                          color: 'red',
                        },
                      },
                    },
                  }}
                >
                  {item.submenu.map((subItem: MenuItemType) => (
                    <MuiMenuItem
                      key={subItem.id}
                      component={Link}
                      to={subItem.path}
                      onClick={() => handleDropdownClose(item.id)}
                      sx={{
                        color: '#000',
                        textTransform: 'none',
                      }}
                    >
                      {subItem.name}
                    </MuiMenuItem>
                  ))}
                </Menu>
              )}
            </Box>
          ))}
        </Box>

        {/* Mobile Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {menuItems.map((item: MenuItemType) => (
            <Box key={item.id}>
              <MuiMenuItem 
                onClick={() => {
                  if (item.submenu) {
                    handleMobileSubmenuToggle(item.id);
                  } else {
                    handleMobileMenuClose();
                  }
                }}
                component={item.submenu ? 'div' : Link}
                to={item.submenu ? undefined : item.path}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: 1 
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getIconComponent(item.icon)}
                  {item.name}
                </Box>
                {item.submenu && (
                  expandedMobileMenus[item.id] ? <ExpandLess /> : <ExpandMore />
                )}
              </MuiMenuItem>
              
              {/* Mobile Submenu */}
              {item.submenu && expandedMobileMenus[item.id] && (
                <Box sx={{ pl: 4 }}>
                  {item.submenu.map((subItem: MenuItemType) => (
                    <MuiMenuItem 
                      key={subItem.id}
                      component={Link}
                      to={subItem.path}
                      onClick={handleMobileMenuClose}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: '0.9rem'
                      }}
                    >
                      {getIconComponent(subItem.icon)}
                      {subItem.name}
                    </MuiMenuItem>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
